import { useCallback, useEffect, useRef } from 'react';
import { createHeadlinerVideoTranscriptionWizardRedirect } from 'api';
import { useAuth } from 'state/auth';
import { AspectRatioName, AudioSelection } from 'types';
import { formatDuration } from 'utils/time';
import useExportVideo from './useExportVideo';

export interface UseExportVideoToHeadlinerConfig {
  projectName?: string;
  startMillis?: number;
  endMillis?: number;
  aspectRatio?: AspectRatioName;
}

export interface ExportVideoToHeadlinerArgs {
  projectCuid: string;
  selection: AudioSelection;
  isClip: boolean;
  onSuccess?: (redirectUrl: string) => void;
}

function buildClipTitle(
  projectName: string | undefined,
  startMillis: number,
  endMillis: number,
): string | undefined {
  if (!projectName) {
    return undefined;
  }

  return `${projectName}, Clip (${formatDuration(
    startMillis,
  )} - ${formatDuration(endMillis)})`;
}

export default function useExportVideoToHeadliner({
  projectName,
  startMillis = 0,
  endMillis = 0,
  aspectRatio,
}: UseExportVideoToHeadlinerConfig = {}) {
  const { accessToken } = useAuth();
  const argsRef = useRef<ExportVideoToHeadlinerArgs>();
  const successRef = useRef(false);
  const { data: videoExport, exportVideo, status } = useExportVideo();

  const clipTitle = buildClipTitle(projectName, startMillis, endMillis);

  useEffect(() => {
    if (
      videoExport?.videoUrl &&
      argsRef.current &&
      accessToken &&
      !successRef.current
    ) {
      const { projectCuid, selection } = argsRef.current;

      const redirectUrl = createHeadlinerVideoTranscriptionWizardRedirect({
        accessToken,
        projectCuid,
        clipTitle,
        aspectRatio,
        videoUrl: videoExport.videoUrl,
        endWordId: selection.endWordId,
        startWordId: selection.startWordId,
      });

      argsRef.current.onSuccess?.(redirectUrl);
      successRef.current = true;
    }
  }, [accessToken, aspectRatio, clipTitle, videoExport?.videoUrl]);

  const exportVideoToHeadliner = useCallback(
    (args: ExportVideoToHeadlinerArgs) => {
      const { projectCuid, selection } = args;
      argsRef.current = args;
      successRef.current = false;

      exportVideo({ projectCuid, selection });
    },
    [exportVideo],
  );

  return { exportVideoToHeadliner, status };
}
